import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gift, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase, RedeemVoucherResult } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface RedeemVoucherProps {
  userId: string;
}

const RedeemVoucher = ({ userId }: RedeemVoucherProps) => {
  const [voucherCode, setVoucherCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!voucherCode.trim()) {
      toast({
        title: "Missing code",
        description: "Please enter a voucher code",
        variant: "destructive"
      });
      return;
    }
    
    setIsRedeeming(true);
    
    try {
      // Check if user has already used this voucher code
      const { data: existingUsage, error: usageError } = await supabase
        .from('vouchers')
        .select('*')
        .eq('code', voucherCode.toUpperCase())
        .eq('user_id', userId)
        .maybeSingle();
        
      if (usageError) {
        toast({
          title: "Error",
          description: "Failed to check voucher usage",
          variant: "destructive"
        });
      } else if (existingUsage) {
        toast({
          title: "Already used",
          description: "You have already used this voucher code",
          variant: "destructive"
        });
        setIsRedeeming(false);
        return;
      }
      
      // Call redeem_voucher function with proper casting
      const { data, error } = await supabase.rpc(
        'redeem_voucher',
        { 
          p_code: voucherCode.toUpperCase(),
          p_user_id: userId
        }
      );
      
      if (error) throw error;
      
      // Cast the data to unknown first, then to our defined type to avoid type errors
      const result = data as unknown as RedeemVoucherResult;
      
      if (result.success) {
        toast({
          title: "Success!",
          description: result.message,
        });
        setVoucherCode('');
        
        // Refresh user credits data and premium vouchers
        queryClient.invalidateQueries({ queryKey: ['userCredits'] });
        queryClient.invalidateQueries({ queryKey: ['premiumVouchers'] });
        queryClient.invalidateQueries({ queryKey: ['userListings'] });
      } else {
        toast({
          title: "Error",
          description: result.message || "Invalid voucher code",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to redeem voucher code",
        variant: "destructive"
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Gift className="mr-2 h-5 w-5 text-brand-500" />
          Aktivizo Kuponin
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleRedeem} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="voucher-code">Kodi i Kuponit</Label>
            <div className="flex gap-2">
              <Input
                id="voucher-code"
                value={voucherCode}
                onChange={e => setVoucherCode(e.target.value.toUpperCase())}
                placeholder="VOUCHER123"
                className="flex-1"
              />
              <Button 
                type="submit"
                disabled={isRedeeming || !voucherCode.trim()}
              >
                {isRedeeming ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Duke aktivizuar...
                  </>
                ) : (
                  'Aktivizo'
                )}
              </Button>
            </div>
            <p className="text-sm text-gray-500">
              Vendosni kodin e kuponit për të aktivizuar kreditet shtesë, monedha ose veçorit premium
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default RedeemVoucher;
